import React from 'react'
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google'
import { useAuth } from '../../hooks/useAuth'

interface LoginButtonProps {
  onSuccess?: () => void
  onError?: (msg: string) => void
  compact?: boolean
}

export const LoginButton: React.FC<LoginButtonProps> = ({
  onSuccess,
  onError,
  compact = false,
}) => {
  const { signInWithGoogle } = useAuth()
  const [loading, setLoading] = React.useState(false)

  const handleSuccess = async (response: CredentialResponse) => {
    if (!response.credential) {
      onError?.('No credential returned from Google')
      return
    }
    setLoading(true)
    try {
      await signInWithGoogle(response.credential)
      onSuccess?.()
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Sign-in failed')
    } finally {
      setLoading(false)
    }
  }

  if (compact) {
    // Render the real Google button first (so it initialises at its natural size and
    // receives pointer events), then place our themed label on top with
    // pointer-events:none so clicks fall through to the Google button underneath.
    // The outer container is clipped to the same height as ThemeToggle
    // (padding:4px 10px + font-size:0.75rem ≈ 26px) and just wide enough for "G".
    return (
      <>
        <style>{`
          .login-btn-compact-outer {
            position: relative;
            display: inline-block;
            width: 28px;
            height: calc(0.75rem * 1.5 + 4px * 2);
            overflow: hidden;
            cursor: pointer;
            border-radius: var(--radius-sm, 4px);
          }
          .login-btn-compact-label {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            background: var(--bg-primary, #0d1117);
            border: 0.5px solid var(--border-color-solid, #30363d);
            color: var(--text-secondary, #8b949e);
            font-family: 'JetBrains Mono', 'Courier New', monospace;
            font-size: 0.75rem;
            border-radius: var(--radius-sm, 4px);
            pointer-events: none;
            transition: border-color 0.15s ease, color 0.15s ease;
            z-index: 1;
          }
          .login-btn-compact-outer:hover .login-btn-compact-label {
            border-color: var(--accent-blue);
            color: var(--accent-blue);
          }
        `}</style>
        <div className="login-btn-compact-outer" title="Sign in with Google">
          <GoogleLogin
            onSuccess={handleSuccess}
            onError={() => onError?.('Google sign-in failed')}
            size="small"
            shape="rectangular"
            text="signin"
          />
          <div className="login-btn-compact-label">
            {loading ? <span>…</span> : <span>G</span>}
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <style>{`
        .login-btn-wrap {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.78rem;
          color: var(--text-secondary);
        }
        .login-btn-label {
          white-space: nowrap;
        }
      `}</style>
      <div className="login-btn-wrap">
        {loading ? (
          <span className="login-btn-label">signing in...</span>
        ) : (
          <>
            <span className="login-btn-label">[sign in with</span>
            <GoogleLogin
              onSuccess={handleSuccess}
              onError={() => onError?.('Google sign-in failed')}
              size="small"
              shape="pill"
              text="signin_with"
            />
            <span className="login-btn-label">]</span>
          </>
        )}
      </div>
    </>
  )
}
